import { useEffect, useRef, type ReactNode } from 'react'
import type { Role } from '@lms/shared-types'
import { Sidebar } from './Sidebar'
import { Avatar } from '../primitives/Avatar'
import { roleLabel } from '../../lib/role'

/**
 * Mobile slide-out drawer for the shell.
 *
 * Used at viewport widths <720px where the sidebar can't sit alongside main
 * content. Triggered from the TopBar hamburger button. Contains the same
 * Sidebar contents (full labels, no icon-rail collapse) plus the signed-in
 * identity block + sign-out so the TopBar can drop those on small screens.
 *
 * Behaviour:
 *   - slides in from the left, ~280px wide, with a dimmed backdrop
 *   - tap on backdrop OR Escape closes
 *   - traps Tab focus inside the drawer while open
 *   - respects prefers-reduced-motion (snaps in/out, no animation)
 *   - role="dialog" aria-modal="true" aria-label="Navigation"
 */

export interface MobileDrawerProps {
  open: boolean
  onClose: () => void
  currentRole: Role
  /** Full role set — passed through to Sidebar so multi-role users see
   *  nav items for all their roles. Falls back to [currentRole] when absent. */
  currentRoles?: Role[]
  userName: string
  userEmail: string
  /** The user's team name — shown as the footer identity badge in place of
   *  the role (matches the desktop ProfileMenu). Falls back to the role
   *  label(s) when absent. */
  teamName?: string | null
  onSignOut: () => void
}

const DRAWER_WIDTH = 280

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function MobileDrawer({
  open,
  onClose,
  currentRole,
  currentRoles,
  userName,
  userEmail,
  teamName,
  onSignOut,
}: MobileDrawerProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return undefined

    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key === 'Tab') {
        const root = panelRef.current
        if (!root) return
        const focusables = root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (!first || !last) return
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKey)
    const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    focusables?.[0]?.focus()

    return () => {
      document.removeEventListener('keydown', handleKey)
      previouslyFocused.current?.focus()
    }
  }, [open, onClose])

  if (!open) return null

  const reduced = prefersReducedMotion()
  const transition = reduced
    ? 'none'
    : 'transform var(--motion-duration-base) var(--motion-ease-out),' +
      ' opacity var(--motion-duration-base) var(--motion-ease-out)'

  return (
    <div
      role="presentation"
      data-testid="mobile-drawer-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(42,38,34,0.45)',
        zIndex: 90,
        opacity: 1,
        transition,
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        data-testid="mobile-drawer"
        onClick={(event) => event.stopPropagation()}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: DRAWER_WIDTH,
          maxWidth: '85vw',
          background: 'var(--surface)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
          transform: 'translateX(0)',
          transition,
          zIndex: 91,
        }}
      >
        <DrawerHeader onClose={onClose} />
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Sidebar
            currentRole={currentRole}
            {...(currentRoles != null ? { currentRoles } : {})}
            variant="drawer"
          />
        </div>
        <DrawerFooter
          userName={userName}
          userEmail={userEmail}
          currentRole={currentRole}
          {...(currentRoles != null ? { currentRoles } : {})}
          teamName={teamName}
          onSignOut={onSignOut}
        />
      </div>
    </div>
  )
}

interface DrawerHeaderProps {
  onClose: () => void
}

function DrawerHeader({ onClose }: DrawerHeaderProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid var(--french-gray-2)',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--text)',
        }}
      >
        Leave Management
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close navigation"
        style={{
          minWidth: 44,
          minHeight: 44,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          color: 'var(--text-2)',
          background: 'transparent',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </div>
  )
}

interface DrawerFooterProps {
  userName: string
  userEmail: string
  currentRole: Role
  currentRoles?: Role[]
  teamName?: string | null | undefined
  onSignOut: () => void
}

function DrawerFooter({
  userName,
  userEmail,
  currentRole,
  currentRoles,
  teamName,
  onSignOut,
}: DrawerFooterProps): JSX.Element {
  const displayRoles = currentRoles && currentRoles.length > 0 ? currentRoles : [currentRole]
  return (
    <FooterContainer>
      <Avatar name={userName} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={userName}
        >
          {userName}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          {teamName ? (
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{teamName}</span>
          ) : (
            displayRoles.map((r) => (
              <span
                key={r}
                style={{
                  fontSize: 11,
                  color: 'var(--text-3)',
                }}
              >
                {roleLabel(r)}
              </span>
            ))
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onSignOut}
        style={{
          minHeight: 36,
          padding: '8px 12px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text)',
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          cursor: 'pointer',
        }}
      >
        Sign out
      </button>
    </FooterContainer>
  )
}

function FooterContainer({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 12,
        borderTop: '1px solid var(--french-gray-2)',
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  )
}
